import { PayloadAction, createSlice } from '@reduxjs/toolkit'

import { ClientType } from 'feature/user/types'

import {
  clearClientType,
  getAuthRefreshAccessToken,
  getAuthStatus,
  getClientType,
  setAuthRefreshAccessToken,
  setAuthStatus,
  setClientType,
} from './authStorage'

// статус авторизации пользователя
export enum AuthStatus {
  // гость - новый пользователь (не вводил номер)
  guest = 'guest',
  // подтвердил смс ФЛ (но не ввел данные фамилия, имя и т.п.)
  smsConfirmed = 'smsConfirmed',
  // подтвердил смс ЮЛ (переход на регистрацию компании)
  smsConfirmedCompany = 'smsConfirmedCompany',
  // полностью зарегистрирован (доступ ко всем разделам)
  registered = 'registered',
}

// статус ошибки запроса
export enum AuthError {
  // токен просрочен
  tokenExpired = 401,
  // доступ запрещен
  accessDenied = 403,
  // пользователь залакирован (не активен в бд)
  userWasBlock = 423.1,
  // у пользоветля отозвано опд
  revokePersonalData = 423.2,
}

// статус смс сообщения (для авторизации и т.п.)
export enum SmsStatus {
  // готова к отправки (смс еще не отправлено)
  readyForSend = 'readyForSend',
  // смс отправлено
  sendedCode = 'sendedCode',
  // введен неверный код
  wrongCode = 'wrongCode',
  // введен корректный код
  successCode = 'successCode',
}

// статус блокировки по смс
export enum SmsBlockType {
  // время вышло (60 сек на ввод смс)
  timeNotRunout = 'time not expired',
  // лимит смс (3 смс в 10 минут, если запросить 4 то будет этот блок)
  countLimit = 'limit of sms',
}

export type AuthType = {
  authInfo: {
    accessToken: string | null
    refreshToken: string | null
    status: AuthStatus
    error: AuthError | null
    isLoading: boolean
    clientType: ClientType | null
  }
  userInfo: {
    phone: string
    isDataAgreeRequired: boolean
    hasUnreadNotifications: boolean
  }
  smsInfo: {
    status: SmsStatus
    error: string | null
    blockTime: number
    blockType: SmsBlockType | null
  }
}

const initialState: AuthType = {
  // авторизационная информация
  authInfo: {
    // аксес токен (хранится только в памяти, не в localStorage)
    accessToken: null,
    // рефреш токен
    refreshToken: getAuthRefreshAccessToken(),
    // текущий статус авторизации пользователя
    status: getAuthStatus(),
    // текущая ошибка авторизации пользователя
    error: null,
    // идет ли обращения к ручкам
    isLoading: false,
    // тип клиента (personal / company), инициализируется из localStorage
    clientType: getClientType(),
  },
  // дополнительная информация пользователя
  userInfo: {
    // номер телефона
    phone: '',
    // нужно ли дополнительное согласие
    isDataAgreeRequired: false,
    // есть ли непрочитанные уведомления
    hasUnreadNotifications: false,
  },
  // информация смс пользователя
  smsInfo: {
    // текущий статус
    status: SmsStatus.readyForSend,
    // текущая ошибка
    error: null,
    // время блокировки
    blockTime: 0,
    // тип блокировки
    blockType: null,
  },
}

// слайс для авторизации пользователя
export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    // установить автаризационную информацию
    setAuthInfo: (
      state,
      action: PayloadAction<{
        accessToken?: string | null
        refreshToken?: string | null
        status?: AuthStatus
        error?: AuthError | null
        isLoading?: boolean
      }>,
    ) => {
      if (action.payload.accessToken !== undefined) {
        state.authInfo.accessToken = action.payload.accessToken
      }
      if (action.payload.refreshToken !== undefined) {
        state.authInfo.refreshToken = action.payload.refreshToken
        setAuthRefreshAccessToken(action.payload.refreshToken)
      }
      if (action.payload.status !== undefined) {
        state.authInfo.status = action.payload.status
        setAuthStatus(action.payload.status)
      }
      if (action.payload.error !== undefined)
        state.authInfo.error = action.payload.error
      if (action.payload.isLoading !== undefined)
        state.authInfo.isLoading = action.payload.isLoading
    },
    // установить дополнительную пользовательскую информацию.
    setUserInfo: (
      state,
      action: PayloadAction<{
        phone?: string
        isDataAgreeRequired?: boolean
      }>,
    ) => {
      if (action.payload.isDataAgreeRequired !== undefined)
        state.userInfo.isDataAgreeRequired = action.payload.isDataAgreeRequired
      if (action.payload.phone !== undefined)
        state.userInfo.phone = action.payload.phone
    },
    // установить смс информацию
    setSmsInfo: (
      state,
      action: PayloadAction<{
        status?: SmsStatus
        error?: string | null
        blockTime?: number
        blockType?: SmsBlockType | null
      }>,
    ) => {
      if (action.payload.blockTime !== undefined)
        state.smsInfo.blockTime = action.payload.blockTime
      if (action.payload.blockType !== undefined)
        state.smsInfo.blockType = action.payload.blockType
      if (action.payload.error !== undefined)
        state.smsInfo.error = action.payload.error
      if (action.payload.status !== undefined)
        state.smsInfo.status = action.payload.status
    },
    // установить не прочитанные уведомления
    setHasUnreadNotifications: (state, action: PayloadAction<boolean>) => {
      state.userInfo.hasUnreadNotifications = action.payload
    },
    // установить тип клиента (ФЛ/ЮЛ)
    setClientTypeInfo: (state, action: PayloadAction<ClientType | null>) => {
      state.authInfo.clientType = action.payload
      if (action.payload) {
        setClientType(action.payload)
      } else {
        clearClientType()
      }
    },
  },
})

export const { actions, reducer } = authSlice
export const authActions = actions
export const authReducer = reducer
